import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { INVENTORY_CATEGORY_UNIT } from '@wrapforce/shared/constants';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { useBrands, useCreateBrand, useCreateInventoryItem } from '@/features/inventory/api';

// Shared across the ceramic/ppf/tint line builders (each already fetches
// useInventory({ category }) + useBrands and only offered a Link to
// /app/inventory when empty) - lets a shop create the first stocked item for
// that category without leaving the quote, mirroring MaterialPicker's wrap
// equivalent (AddInventoryLinkDialog).
export function AddInventoryItemDialog({
  open, onOpenChange, workspaceId, category, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspaceId: string | null;
  category: 'ceramic' | 'ppf' | 'tint' | 'print';
  onCreated: (itemId: string) => void;
}) {
  const { t } = useTranslation('quotes');
  const { data: brandsData } = useBrands(workspaceId);
  const createBrand = useCreateBrand(workspaceId);
  const createItem = useCreateInventoryItem(workspaceId);
  const unit = INVENTORY_CATEGORY_UNIT[category] ?? 'sqft';

  const [brandId, setBrandId] = useState('');
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [name, setName] = useState('');
  const [priceDollars, setPriceDollars] = useState('');
  const [initialQty, setInitialQty] = useState(0);
  const [reorderPoint, setReorderPoint] = useState(0);

  const brandOptions = (brandsData?.items ?? []).map((b) => ({ value: b.id, label: b.name }));

  useEffect(() => {
    if (!open) return;
    setBrandId('');
    setAddingBrand(false);
    setNewBrandName('');
    setName('');
    setPriceDollars('');
    setInitialQty(0);
    setReorderPoint(0);
  }, [open]);

  async function handleCreateBrand() {
    if (!newBrandName.trim()) return;
    const { id } = await createBrand.mutateAsync({ name: newBrandName.trim() });
    setBrandId(id);
    setNewBrandName('');
    setAddingBrand(false);
    toast.success(t('inventory:toast.brandAdded'));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!workspaceId || !name.trim()) return;
    const priceCentsPerUnit = priceDollars ? Math.round(Number(priceDollars) * 100) : undefined;
    const created = await createItem.mutateAsync({
      name: name.trim(),
      category,
      brandId: brandId || undefined,
      unit,
      priceCentsPerUnit,
      initialQty,
      reorderPoint,
    });
    toast.success(t('pricing.addInventoryDialog.toastAdded'));
    onCreated(created.id);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('pricing.addInventoryDialog.title')}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="add-inv-brand">{t('pricing.addInventoryDialog.brand')}</Label>
            <button type="button" onClick={() => setAddingBrand((v) => !v)} className="text-[11px] font-semibold text-accent-ink hover:underline">
              {addingBrand ? t('common:actions.cancel') : t('pricing.addInventoryDialog.addNewBrand')}
            </button>
          </div>
          <Combobox id="add-inv-brand" value={brandId} onChange={setBrandId} options={brandOptions} placeholder={t('pricing.noBrand')} />
          {addingBrand ? (
            <div className="flex gap-2">
              <Input
                placeholder={t('pricing.addInventoryDialog.newBrandPlaceholder')}
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateBrand()}
                className="flex-1"
              />
              <Button type="button" size="sm" disabled={!newBrandName.trim()} loading={createBrand.isPending} onClick={handleCreateBrand}>
                {t('common:actions.save')}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="add-inv-name">{t('pricing.addInventoryDialog.itemName')}</Label>
          <Input id="add-inv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('pricing.addInventoryDialog.itemNamePlaceholder')} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="add-inv-price">{t(unit === 'bottle' ? 'pricing.addInventoryDialog.pricePerBottle' : 'pricing.addInventoryDialog.pricePerSqft')}</Label>
          <Input id="add-inv-price" type="number" step="0.01" min="0" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="add-inv-qty">{t('pricing.addInventoryDialog.onHand')}</Label>
            <Input id="add-inv-qty" type="number" step="0.01" value={initialQty} onChange={(e) => setInitialQty(Number(e.target.value))} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="add-inv-reorder">{t('pricing.addInventoryDialog.reorderAt')}</Label>
            <Input id="add-inv-reorder" type="number" step="0.01" value={reorderPoint} onChange={(e) => setReorderPoint(Number(e.target.value))} />
          </div>
        </div>
        <Button type="submit" loading={createItem.isPending} disabled={!name.trim()} className="mt-1 w-full">
          {t('pricing.addInventoryDialog.submit')}
        </Button>
      </form>
    </Dialog>
  );
}
